import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BrushCleaning, Loader2, ScanEye, Search, X } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useUser } from '../../../contexts/UserContext';
import { useJourneys } from '../../../services/journeys';
import './EagleEye.css';

// Eagle Eye (Nic 2026-09-28): every person's whole journey, lead in to
// Claims Manager, one row each, left to right, with the time of each mark.
// Read-only. The icon shows on Sales and Client Onboarding for pipeline:view.

const TZ = 'America/Chicago';
// Live, not an archive (Nic 2026-09-28): To CM and Lost hold the last 30
// days; search reaches everyone, whenever they finished.
const BUCKETS = [
  ['active', 'In progress'],
  ['nurture', 'Nurture'],
  ['to_cm', 'To CM'],
  ['lost', 'Lost'],
];
// Column labels, short enough for one line each.
const SALES_SHORT = {
  lead_in: 'Lead in',
  with_sales: 'Sales',
  contacted: 'Contacted',
  booked: 'Booked',
  signup_link: 'Signup link',
  account: 'Account',
  signed: 'Signed',
};
// The Now column's status dot.
const TONE_DOT = {
  active: 'bg-sky-500',
  alert: 'bg-amber-500',
  ready: 'bg-emerald-500',
  done: 'bg-emerald-500',
  nurture: 'bg-violet-500',
  dead: 'bg-rose-500',
  waiting: 'bg-slate-300 dark:!bg-slate-500',
};

const shortDate = (at) =>
  at ? new Date(at).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: TZ }) : '';
const fullTime = (at) =>
  at
    ? new Date(at).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: TZ,
      })
    : '';
const ago = (at) => {
  if (!at) return '';
  const ms = Date.now() - new Date(at).getTime();
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(ms / 3600000);
  return hours >= 1 ? `${hours}h` : 'now';
};
const fmtPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p || '';
};

// The row as one sequence of nodes, so the connector and "you are here"
// can be worked out in one place. Paperwork is one node (all steps done).
const nodesOf = (p) => {
  const onb = Object.fromEntries((p.onboarding || []).map((m) => [m.id, m]));
  const paperDone = p.checklist.length > 0 && p.checklist.every((s) => s.status === 'pass');
  return [
    ...p.sales.map((m) => ({ ...m, phase: 'sales', reached: !!m.at })),
    { id: 'taken', phase: 'onb', reached: !!onb.taken?.at, ...onb.taken },
    { id: 'paperwork', phase: 'onb', reached: paperDone },
    { id: 'ready', phase: 'onb', reached: !!onb.ready?.at, ...onb.ready },
    { id: 'with_cm', phase: 'onb', reached: !!onb.with_cm?.at, ...onb.with_cm },
  ];
};

const Tooltip = ({ tip }) =>
  tip ? (
    <div
      className="pointer-events-none fixed z-[1070] max-w-[16rem] -translate-x-1/2 -translate-y-full rounded-lg bg-slate-900 px-2.5 py-1.5 text-[11px] leading-snug !text-white shadow-lg dark:!bg-slate-700 dark:ring-1 dark:ring-white/15"
      style={{ left: tip.x, top: tip.y - 8 }}
    >
      {tip.lines.map((l, i) => (
        <div key={i} className={i === 0 ? 'font-semibold' : 'opacity-80'}>
          {l}
        </div>
      ))}
    </div>
  ) : null;

// A quiet layout (Nic 2026-09-28: the first pass tried too hard). Future
// steps are faint ticks on a hairline, reached steps small solid dots, one
// ring marks what is next. Phase color lives only on the progress itself.
const PHASE = {
  sales: {
    label: 'Sales',
    dot: 'bg-sky-500',
    line: 'bg-sky-400',
    soft: 'bg-sky-500/40',
    text: 'text-sky-600 dark:text-sky-300',
    rule: 'border-sky-400/50',
    ring: 'rgba(14,165,233,0.95)',
  },
  onb: {
    label: 'Onboarding Workspace',
    dot: 'bg-violet-500',
    line: 'bg-violet-400',
    soft: 'bg-violet-500/40',
    text: 'text-violet-600 dark:text-violet-300',
    rule: 'border-violet-400/50',
    ring: 'rgba(139,92,246,0.95)',
  },
};
const TICK = 'h-1 w-1 bg-slate-300 dark:!bg-white/20';

// ONE column template shared by the header and every row, so each dot is
// centered under its label at any width. Columns stretch evenly; below the
// minimum the list scrolls sideways with the name pinned.
const COLS = [
  '12.5rem', // person
  'repeat(7, minmax(3rem, 1fr))', // sales
  '1.5rem', // the handoff gap
  'minmax(3rem, 1fr)', // taken
  'minmax(5.5rem, 1.6fr)', // paperwork
  'minmax(3rem, 1fr)', // ready
  'minmax(3rem, 1fr)', // with CM
  'minmax(14rem, 1.8fr)', // now
].join(' ');
const MIN_WIDTH = 1040;
const gridStyle = { gridTemplateColumns: COLS };
// Spacing here is exact px / arbitrary values on purpose: Bootstrap also
// defines px-5, py-3, mx-3, mt-4 (bigger, with !important) and wins.
const NAME_COL = 'sticky left-0 z-10 bg-white dark:!bg-surface-elevated';

// A line into and out of the mark. The first mark of a phase has nothing on
// its left and the last nothing on its right, so each phase's line stays its
// own. Flex halves (not absolute rails) keep the mark dead center.
const Track = ({ phase, left, right, first, last, children }) => {
  const half = (on, hidden) =>
    cn(
      'flex-1',
      hidden ? 'bg-transparent' : on ? cn('h-[2px]', PHASE[phase].line) : 'h-px bg-slate-200 dark:!bg-white/10',
    );
  return (
    <div className="flex h-4 w-full items-center">
      <span className={half(left, first)} />
      {children}
      <span className={half(right, last)} />
    </div>
  );
};

const tipAt = (e, lines, onTip) => {
  const r = e.currentTarget.getBoundingClientRect();
  onTip({ x: r.left + r.width / 2, y: r.top, lines });
};

// One milestone: its mark on the line, the date under it once reached.
const Dot = ({ node, left, right, first, last, current, onTip }) => {
  const ph = PHASE[node.phase];
  const lines = [
    node.label,
    node.at
      ? fullTime(node.at)
      : node.direct
        ? 'Signed up directly, without our link'
        : node.noRecord
          ? 'No record (before this was tracked)'
          : node.skipped
            ? 'Skipped'
            : 'Not yet',
    ...(node.by ? [node.id === 'lead_in' ? `Source: ${node.by}` : `By ${node.by}`] : []),
  ];
  return (
    <div className="flex flex-col items-center">
      <Track phase={node.phase} left={left} right={right} first={first} last={last}>
        {/* A roomy hover target around a small mark. */}
        <span
          onMouseEnter={(e) => tipAt(e, lines, onTip)}
          onMouseLeave={() => onTip(null)}
          className="flex h-4 w-4 shrink-0 items-center justify-center"
        >
          <span
            className={cn(
              'rounded-full',
              node.reached
                ? cn('h-2 w-2', ph.dot)
                : current
                  ? 'h-2.5 w-2.5 bg-white dark:!bg-surface-elevated'
                  : node.skipped
                    ? cn('h-1.5 w-1.5', ph.soft)
                    : TICK,
            )}
            style={current && !node.reached ? { boxShadow: `0 0 0 1.5px ${ph.ring}` } : undefined}
          />
        </span>
      </Track>
      <div className="mt-1 h-3 text-[10px] leading-3 tabular-nums text-slate-400">
        {shortDate(node.at)}
      </div>
    </div>
  );
};

// The checklist as one slim segmented bar: violet done, red blocked, gray
// not yet. While paperwork is where they are, the next step is outlined.
const Paperwork = ({ person, left, right, current, onTip }) => {
  const ph = PHASE.onb;
  const steps = person.checklist;
  const done = steps.filter((x) => x.status === 'pass').length;
  const nextId = current ? steps.find((x) => x.status === 'pending')?.id : null;
  const list = steps.length ? steps : Array.from({ length: 9 }, (_, i) => ({ id: i }));
  return (
    <div className="flex flex-col items-center">
      <Track phase="onb" left={left} right={right}>
        <span className="mx-1 flex shrink-0 items-center gap-[3px]">
          {list.map((x) => (
            <span
              key={x.id}
              onMouseEnter={(e) =>
                x.label &&
                tipAt(
                  e,
                  [
                    x.label,
                    x.status === 'pass'
                      ? x.at
                        ? fullTime(x.at)
                        : 'Complete'
                      : x.status === 'fail'
                        ? 'Blocked (answered No)'
                        : 'Not yet',
                    ...(x.by ? [`By ${x.by}`] : []),
                  ],
                  onTip,
                )
              }
              onMouseLeave={() => onTip(null)}
              className="flex h-4 items-center"
            >
              <span
                className={cn(
                  'h-1 w-2 rounded-full',
                  x.status === 'pass'
                    ? ph.dot
                    : x.status === 'fail'
                      ? 'bg-rose-500'
                      : 'bg-slate-200 dark:!bg-white/15',
                )}
                style={x.id === nextId ? { boxShadow: `0 0 0 1px ${ph.ring}` } : undefined}
              />
            </span>
          ))}
        </span>
      </Track>
      <div className="mt-1 h-3 text-[10px] leading-3 tabular-nums text-slate-400">
        {steps.length ? `${done} of ${steps.length}` : ''}
      </div>
    </div>
  );
};

const Row = ({ person, onTip }) => {
  const nodes = nodesOf(person);
  const lastReached = nodes.reduce((acc, n, i) => (n.reached ? i : acc), -1);
  // Before the furthest mark reached, an unreached mark was either skipped
  // (a signup link sent with no meeting booked) or, for a client who never
  // came through as a lead, simply has no record. Never guessed as done.
  const hasLead = !!person.sales[0]?.at;
  nodes.forEach((n, i) => {
    const before = !n.reached && i < lastReached;
    n.noRecord = before && n.phase === 'sales' && !hasLead;
    n.skipped = before && !n.noRecord;
  });
  const passed = (n) => n.reached || n.skipped;
  // What is next: the mark after the furthest one reached, for anyone
  // still moving.
  const currentIdx =
    person.bucket === 'active' && lastReached < nodes.length - 1 ? lastReached + 1 : -1;
  const edge = (i) => {
    const prev = nodes[i - 1];
    const next = nodes[i + 1];
    const first = !prev || prev.phase !== nodes[i].phase;
    const last = !next || next.phase !== nodes[i].phase;
    return {
      first,
      last,
      left: !first && passed(nodes[i]) && passed(prev),
      right: !last && passed(nodes[i]) && passed(next),
      current: i === currentIdx,
    };
  };
  const now = person.now || {};
  return (
    <div
      className="group grid items-center border-b border-slate-100 hover:bg-slate-900/[0.025] dark:border-white/[0.06] dark:hover:bg-white/[0.03]"
      style={gridStyle}
    >
      <div className={cn(NAME_COL, 'self-stretch px-[18px] py-[11px]')}>
        {/* The row's hover tint, over the pinned column's solid white. */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 group-hover:bg-slate-900/[0.025] dark:group-hover:bg-white/[0.03]"
        />
        <div className="relative truncate text-[13px] font-semibold text-slate-800 dark:text-slate-100">
          {person.name}
        </div>
        <div className="relative truncate text-[11px] text-slate-400">
          {[fmtPhone(person.phone), person.email].filter(Boolean).join(' · ') || 'No contact on file'}
        </div>
      </div>
      {nodes.map((n, i) => (
        <React.Fragment key={n.id}>
          {/* The handoff gap between the phases. */}
          {n.id === 'taken' ? <div /> : null}
          {n.id === 'paperwork' ? (
            <Paperwork person={person} {...edge(i)} onTip={onTip} />
          ) : (
            <Dot node={n} {...edge(i)} onTip={onTip} />
          )}
        </React.Fragment>
      ))}
      <div className="min-w-0 px-[18px] py-[11px]">
        <div className="flex items-center gap-2 text-[12px]">
          <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TONE_DOT[now.tone] || TONE_DOT.waiting)} />
          <span className="truncate font-medium text-slate-700 dark:text-slate-200">{now.label}</span>
          {now.since ? (
            <span
              className="shrink-0 whitespace-nowrap tabular-nums text-slate-400"
              title={fullTime(now.since)}
            >
              {ago(now.since)}
            </span>
          ) : null}
        </div>
        {now.detail ? (
          <div className="mt-0.5 truncate pl-3.5 text-[11px] text-slate-400">{now.detail}</div>
        ) : null}
      </div>
    </div>
  );
};

// Two header lines on the same grid: the phase names (a thin rule in their
// color across their columns), then each column's label, centered.
const HeaderRow = () => {
  const label = 'pb-2.5 text-center text-[10px] font-medium text-slate-400';
  return (
    <div
      className="sticky top-0 z-20 grid border-b border-slate-200 bg-white dark:border-white/10 dark:!bg-surface-elevated"
      style={{ ...gridStyle, gridTemplateRows: 'auto auto' }}
    >
      <div
        className={cn(NAME_COL, 'flex items-end px-[18px] pb-2.5 text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400')}
        style={{ gridRow: '1 / span 2' }}
      >
        Person
      </div>
      {[
        ['sales', '2 / span 7'],
        ['onb', '10 / span 4'],
      ].map(([key, span]) => (
        <div
          key={key}
          className={cn(
            'mx-[12px] mb-2 mt-[14px] border-b pb-1.5 text-center text-[10px] font-semibold uppercase tracking-[0.1em]',
            PHASE[key].text,
            PHASE[key].rule,
          )}
          style={{ gridColumn: span, gridRow: 1 }}
        >
          {PHASE[key].label}
        </div>
      ))}
      <div
        className="flex items-end px-[18px] pb-2.5 text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400"
        style={{ gridColumn: 14, gridRow: '1 / span 2' }}
      >
        Now
      </div>
      {Object.values(SALES_SHORT).map((l, i) => (
        <div key={l} className={label} style={{ gridColumn: 2 + i, gridRow: 2 }}>
          {l}
        </div>
      ))}
      {['Taken', 'Paperwork', 'Ready', 'With CM'].map((l, i) => (
        <div key={l} className={label} style={{ gridColumn: 10 + i, gridRow: 2 }}>
          {l}
        </div>
      ))}
    </div>
  );
};

const EagleEyeOverlay = ({ onClose }) => {
  const [bucket, setBucket] = useState('active');
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [tip, setTip] = useState(null);

  // Search waits for a pause in typing.
  useEffect(() => {
    const t = setTimeout(() => setQ(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);
  useEffect(() => setPage(1), [bucket, q]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const res = useJourneys({ bucket, q, page });
  const data = res.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return createPortal(
    <div className="fixed inset-0 z-[1060]">
      <div className="absolute inset-0 bg-slate-900/40 dark:bg-black/60" onClick={onClose} aria-hidden />
      {/* Capped and centered: on a big screen it stays a readable card. */}
      <div className="absolute inset-2 mx-auto flex max-w-[1240px] flex-col overflow-hidden rounded-2xl bg-[#F5F7FA] shadow-2xl dark:!bg-surface-page md:inset-6 md:my-auto md:max-h-[880px]">
        <div className="flex flex-wrap items-center gap-2 bg-white px-4 py-3 shadow-[0_1px_3px_rgba(15,23,42,0.06)] dark:!bg-surface-elevated">
          <div className="flex min-w-0 items-center gap-2 text-base font-bold text-slate-800 dark:text-slate-100">
            <ScanEye size={17} className="shrink-0 text-slate-400" />
            <span className="truncate">Eagle Eye</span>
            {res.isFetching ? <Loader2 size={14} className="animate-spin text-slate-400" /> : null}
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="flex max-w-full gap-0.5 overflow-x-auto rounded-full bg-slate-200/70 p-1 [scrollbar-width:none] dark:!bg-white/10 [&::-webkit-scrollbar]:hidden">
              {BUCKETS.map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setBucket(k)}
                  className={cn(
                    'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-[12px] font-semibold outline-none focus:outline-none',
                    bucket === k
                      ? 'bg-white text-slate-900 shadow-sm dark:!bg-surface-elevated dark:text-white'
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                  )}
                >
                  {l}
                  {data?.counts ? (
                    // Search hits on a tab: the same blue ring + soft pulse as
                    // Client Onboarding and Sales, so a match on another tab
                    // is never missed.
                    <span className="relative inline-flex">
                      {q && data.counts[k] > 0 ? (
                        <span
                          aria-hidden
                          className="absolute inset-0 animate-ping-soft rounded-full shadow-[0_0_0_1.5px_rgba(14,165,233,0.9)] motion-reduce:hidden"
                        />
                      ) : null}
                      <span
                        className={cn(
                          'relative min-w-[1.25rem] rounded-full px-1.5 text-center text-[10px] tabular-nums transition-[box-shadow,background-color,color]',
                          q && data.counts[k] > 0
                            ? 'bg-sky-50 text-sky-700 shadow-[0_0_0_1.5px_rgba(14,165,233,0.85),0_0_8px_rgba(14,165,233,0.3)] dark:!bg-sky-500/15 dark:text-sky-200'
                            : 'bg-slate-900/[0.06] text-slate-500 dark:!bg-white/10 dark:text-slate-400',
                        )}
                      >
                        {data.counts[k]}
                      </span>
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
            <label className="relative block w-full sm:w-64">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, phone"
                className={cn(
                  'h-9 w-full rounded-full bg-white pl-8 pr-9 text-sm text-slate-700 outline-none dark:!bg-white/5 dark:text-slate-200',
                  query.trim()
                    ? 'shadow-[0_0_0_1.5px_rgba(14,165,233,0.85)]'
                    : 'ring-1 ring-slate-200 focus:ring-slate-300 dark:ring-white/10',
                )}
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  title="Clear search"
                  className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-sky-600 outline-none hover:bg-sky-50 focus:outline-none dark:text-sky-300 dark:hover:!bg-sky-500/15"
                >
                  <BrushCleaning size={14} />
                </button>
              ) : null}
            </label>
            <button
              type="button"
              onClick={onClose}
              title="Close"
              className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:!bg-white/10"
            >
              <X size={17} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 p-[12px] md:p-[16px]">
          <div
            className="ee-scroll h-full overflow-auto rounded-2xl bg-white ring-1 ring-slate-200/80 dark:!bg-surface-elevated dark:ring-white/10"
            onScroll={() => setTip(null)}
          >
            <div style={{ minWidth: MIN_WIDTH }}>
              <HeaderRow />
              {res.isError ? (
                <div className="p-6 text-center text-sm text-red-600 dark:text-red-400">
                  Eagle Eye could not load. Try again in a moment.
                </div>
              ) : res.isLoading ? (
                <div className="flex justify-center py-16">
                  <Loader2 size={20} className="animate-spin text-slate-400" />
                </div>
              ) : data.people.length === 0 ? (
                <div className="py-16 text-center text-sm text-slate-500 dark:text-slate-400">
                  {q
                    ? 'No one matches that search.'
                    : bucket === 'to_cm' || bucket === 'lost'
                      ? `No one in the last ${data.recentDays} days. Search finds anyone older.`
                      : 'No one here right now.'}
                </div>
              ) : (
                data.people.map((p) => <Row key={p.key} person={p} onTip={setTip} />)
              )}
            </div>
          </div>
        </div>

        {data && data.total > 0 ? (
          <div className="flex items-center justify-between gap-2 border-t border-slate-200/80 bg-white px-4 py-2 text-xs text-slate-500 dark:border-white/10 dark:!bg-surface-elevated dark:text-slate-400">
            <span className="tabular-nums">
              {data.total} {data.total === 1 ? 'person' : 'people'}
              {!q && (bucket === 'to_cm' || bucket === 'lost') ? (
                <span className="text-slate-400">
                  {' '}
                  in the last {data.recentDays} days. Search finds anyone older.
                </span>
              ) : null}
            </span>
            {pages > 1 ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((x) => x - 1)}
                  className="whitespace-nowrap rounded-lg px-2 py-1 font-semibold outline-none hover:bg-slate-100 focus:outline-none disabled:opacity-30 dark:hover:!bg-white/10"
                >
                  Previous
                </button>
                <span className="tabular-nums">
                  Page {page} of {pages}
                </span>
                <button
                  type="button"
                  disabled={page >= pages}
                  onClick={() => setPage((x) => x + 1)}
                  className="whitespace-nowrap rounded-lg px-2 py-1 font-semibold outline-none hover:bg-slate-100 focus:outline-none disabled:opacity-30 dark:hover:!bg-white/10"
                >
                  Next
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <Tooltip tip={tip} />
    </div>,
    document.body,
  );
};

// The header icon. Renders nothing without pipeline:view.
const EagleEyeButton = () => {
  const { hasPermission } = useUser();
  const [open, setOpen] = useState(false);
  if (!hasPermission('pipeline:view')) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Eagle Eye: everyone's journey"
        aria-label="Eagle Eye"
        className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-500 outline-none shadow-[0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-slate-200/80 hover:bg-slate-50 focus:outline-none dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10"
      >
        <ScanEye size={16} />
      </button>
      {open ? <EagleEyeOverlay onClose={() => setOpen(false)} /> : null}
    </>
  );
};

export default EagleEyeButton;
